import type { AllInstructionsData } from './types';

/**
 * Generation modes
 */
export const AI_GENERATION_MODE = {
  suggest: 'suggest',
  recompose: 'recompose',
  advanced: 'advanced',
} as const;
export type AiGenerationMode = (typeof AI_GENERATION_MODE)[keyof typeof AI_GENERATION_MODE];

/**
 * Suggestions generation mode
 */
export const AI_SUGGESTION_MODE = {
  standard: 'standard',
  creative: 'creative',
  auto: 'auto',
  quotes: 'quotes',
  paragraph: 'paragraph',
  list: 'list',
  personalized: 'personalized',
  customFormula: 'custom_formula',
  guided: 'guided', // mode for usage with "generation guidance" templates
  BAB: 'bab',
  FAB: 'fab',
  PPP: 'ppp',
  PAS: 'pas',
  AIDA: 'aida',
  featureList: 'feature_list',
  sms160: 'sms_160',
  sms320: 'sms_320',
  sms480: 'sms_480',

  rewrite: 'rewrite', // this is service mode, only to display button for landing assets
  engaging: 'engaging', // conflict with AI_SUGGESTION_MODE.standard
  emotional_benefits: 'emotional_benefits',
  default: 'default',

  // modes for summarize generation tool
  topic: 'topic',
  tldr: 'tldr',
  summarize: 'summarize',

  hookBodyCta: 'hook_body_cta',
  shortPost: 'short_post',
  storyTelling: 'story_telling',
  qa: 'qa',
  fluent: 'fluent',
  bulletList: 'list',
  clickbait: 'clickbait',
  summary: 'summary',
  question: 'question',
  hook: 'hook',
  callToAction: 'callToAction', // not using cta as it is meant for something else
  fourPs: 'pppp',
  oneTwoThreeFourFormula: 'one_two_three_four',
  attention: 'attention',

  //deprecated
  amazonFeatureList: 'amazon_feature_list',
  amazonDescription: 'amazon_description',

  combination: 'combination',
  keywordDriven: 'keyword_driven',
  benefit: 'benefit',
  feature: 'feature',
  CTA: 'cta',
  brand: 'brand',
  productCategory: 'product_category',
  pain: 'pain',

  regenerate: 'regenerate', //used in blog draft, for regenerating an outline headline
} as const;
export type AiSuggestionMode = (typeof AI_SUGGESTION_MODE)[keyof typeof AI_SUGGESTION_MODE];

/**
 * Recompose generation mode
 * @enum {string}
 */
export const AI_RECOMPOSE_MODE = {
  strict: 'strict',
  relaxed: 'relaxed',
  polished: 'polished', // TODO: this value might be obsolete, check with BE
  tov: 'tov',
  sfv: 'sfv',
  formatGroups: 'format_groups',
  keepAsIs: 'keep_as_is', // keep the original content unchanged

  // currently only used in the "blog" and "long form"
  expand: 'expand',
  shorten: 'shorten',
  refine: 'refine',
  manual: 'manual',
  translate: 'translate',
  instructionBased: 'instruction_based',
} as const;
export type AiRecomposeMode = (typeof AI_RECOMPOSE_MODE)[keyof typeof AI_RECOMPOSE_MODE];

export type SuggestionOrRecomposeMode = AiSuggestionMode | AiRecomposeMode;

export const GENERATION_IMPROVE_MODE = {
  doNotImprove: 'do_not_improve',
  continueWriting: 'continue_writing',

  // "image" modes
  improveImage: 'improve_image',
  changeImageOverlay: 'change_image_overlay',
  changeImageStyle: 'change_image_style',

  // "text" modes
  improveEngaging: 'improve_engaging',
  improveSimilar: 'improve_similar',
  boostScoreEngaging: 'boost_score_engaging',
  boostScoreSimilar: 'boost_score_similar',
  makeCasual: 'make_casual',
  makeLonger: 'make_longer',
  makeShorter: 'make_shorter',
  changeTov: 'change_tov',
  instructions: 'instructions',
  sfv: 'sfv',
  formatGroups: 'format_groups',
  translate: 'translate',
} as const;
export type GenerationImproveMode =
  (typeof GENERATION_IMPROVE_MODE)[keyof typeof GENERATION_IMPROVE_MODE];

export const GENERATION_SOURCE_TYPE = {
  improveScore: 'improve_score',
  improveListScore: 'improve_list_score',
  changeTov: 'change_tov',
  changeAnnotations: 'change_annotations',
} as const;
export type GenerationSourceType =
  (typeof GENERATION_SOURCE_TYPE)[keyof typeof GENERATION_SOURCE_TYPE];

export const GENERATION_MODE_TYPE = {
  common: 'common',
  toneOfVoice: 'toneOfVoice',
  guidance: 'guidance',
  personalized: 'personalized',
  customFormula: 'customFormula',
} as const;
export type GenerationModeType = (typeof GENERATION_MODE_TYPE)[keyof typeof GENERATION_MODE_TYPE];

// TODO: this enum and GENERATION_INSTRUCTION_TYPE are contain parts of SuggestionAnnotation.type from BE, consider refactoring FE side
export const GENERATION_GUIDANCE_TYPE = {
  customerAvatar: 'customer_avatar',
  pain: 'pain',
  benefit: 'benefit',
  feature: 'feature',
  ageRange: 'age_range',
  gender: 'gender',
  keyword: 'keyword',
  campaignKeyword: 'campaign_keyword',
  hashtag: 'hashtag', // TODO: we might not need this type anymore, consider removing it
  hubspot: 'hubspotEmails',
  google: 'google',
  facebook: 'facebook',
  linkedin: 'linkedin',
  linkedinSocial: 'linkedin_social',
  amazon: 'reviewsAmazon',
  trustpilot: 'reviewsTrustpilot',
  landing_page: 'landingPage',
  home_page: 'homePage',
  competitors: 'competitors',
  thoughtLeadership: 'thoughtLeadership',
  productPage: 'productPage',
  other: 'other',
  facebookPages: 'facebook_pages',
  instagram: 'instagram',
  genericResource: 'genericResource',
} as const;
export type GenerationGuidanceType =
  (typeof GENERATION_GUIDANCE_TYPE)[keyof typeof GENERATION_GUIDANCE_TYPE];

export const GENERATION_INSTRUCTION_TYPE = {
  writeAbout: 'write_about',
  example: 'example',
  conclusion: 'conclusion',
  explain: 'explain',
  list: 'list',
} as const;
export type GenerationInstructionType =
  (typeof GENERATION_INSTRUCTION_TYPE)[keyof typeof GENERATION_INSTRUCTION_TYPE];

export const CASING_TYPE = {
  sentence: 'sentence',
  title: 'title',
  upper: 'upper',
  default: 'default',
} as const;
export type CasingType = (typeof CASING_TYPE)[keyof typeof CASING_TYPE];

export const CASING_LABELS = {
  [CASING_TYPE.sentence]: 'Sentence case',
  [CASING_TYPE.upper]: 'UPPERCASE',
  [CASING_TYPE.title]: 'Title Case',
  [CASING_TYPE.default]: 'Let AI decide',
};

export const PERSONALIZED_NAME_MAX_LENGTH = 20;
export const PERSONALIZED_ITEMS_MIN_COUNT = 3;
export const PERSONALIZED_ITEMS_MAX_COUNT = 5;
export const PERSONALIZED_ITEM_MAX_LENGTH = 500;
export const PERSONALIZED_DESCRIPTION_MAX_LENGTH = 100;

export const MAX_WORDS_FOR_SCORE = 200;

export const DEFAULT_NUMBER_OF_VARIATIONS = 5;
export const BASIC_DEFAULT_NUMBER_OF_VARIATIONS = 2;
export const CANVAS_DEFAULT_NUMBER_OF_VARIATIONS = 3;
export const CANVAS_DEFAULT_LP_NUMBER_OF_VARIATIONS = 1;

export const AI_TEXT_SIZE = {
  short: 'short',
  medium: 'medium',
  long: 'long',
} as const;
export type AiTextSize = (typeof AI_TEXT_SIZE)[keyof typeof AI_TEXT_SIZE];

/**
 * Generation languages
 * @enum {string}
 */
export const LANGUAGE = {
  bg: 'bg',
  cs: 'cs',
  zh_tw: 'zh-tw',
  zh_cn: 'zh-cn',
  da: 'da',
  de: 'de',
  de_in: 'de_in',
  de_ch: 'de-ch',
  el: 'el',
  en_us: 'en-us',
  en_gb: 'en-gb',
  es: 'es',
  es_la: 'es-la',
  es_cs: 'es-cs',
  fr: 'fr',
  fr_qu: 'fr-qu',
  hu: 'hu',
  it: 'it',
  ja: 'ja',
  ms: 'ms',
  ko: 'ko',
  nl: 'nl',
  pl: 'pl',
  pt_pt: 'pt-pt',
  pt_br: 'pt-br',
  ro: 'ro',
  ru: 'ru',
  cmn: 'cmn',
  yue: 'yue',
  sv: 'sv',
  th: 'th',
  tr: 'tr',
} as const;
export type VisibleLanguage = (typeof LANGUAGE)[keyof typeof LANGUAGE];

export const DEFAULT_LANGUAGE = LANGUAGE.en_us;

export const HIDDEN_LANGUAGE = {
  he: 'he',
} as const;
export type HiddenLanguage = (typeof HIDDEN_LANGUAGE)[keyof typeof HIDDEN_LANGUAGE];

export type Language = VisibleLanguage | HiddenLanguage;

export const INFORMAL_LANGUAGE: Record<string, Language> = {
  [LANGUAGE.de_in]: LANGUAGE.de,
} as const;

export const EN_LANGUAGES: Language[] = [LANGUAGE.en_gb, LANGUAGE.en_us];

// Filtering the informal languages
export const SUPPORTED_LANGUAGES_COUNT = Object.values(LANGUAGE).filter(
  (l) => !Object.keys(INFORMAL_LANGUAGE).includes(l)
).length;

export const LANGUAGE_TARGET_FORMALITY = {
  formal: 'more', // backend value
  informal: 'less', // backend value
} as const;
export type LanguageTargetFormality =
  (typeof LANGUAGE_TARGET_FORMALITY)[keyof typeof LANGUAGE_TARGET_FORMALITY];

export const ZERO_RESULTS_REASON = {
  onlyImproveScore: 'only_improve_score',
} as const;
export type ZeroResultsReason = (typeof ZERO_RESULTS_REASON)[keyof typeof ZERO_RESULTS_REASON];

export const EMPTY_ALL_INSTRUCTIONS_DATA: AllInstructionsData = {
  html: '<p></p>', // not using `EMPTY_PARAGRAPH_HTML` because of cycle reference TODO: investigate it
  serialized: '',
  placeholders: {},
  text: '',
};

export const PROMPT_INSTRUCTION_TYPE = {
  generic: 'generic',
  vocabulary: 'vocabulary',
  format: 'format',
  formula: 'formula',
} as const;
export type PromptInstructionType =
  (typeof PROMPT_INSTRUCTION_TYPE)[keyof typeof PROMPT_INSTRUCTION_TYPE];

export const isGenericPromptInstructionType = (instructionType: PromptInstructionType): boolean =>
  instructionType === PROMPT_INSTRUCTION_TYPE.generic;

export const PROMPT_GENERIC_INSTRUCTION = {
  sectionHeader: 'sectionHeader',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
} as const;
export type PromptGenericInstruction =
  (typeof PROMPT_GENERIC_INSTRUCTION)[keyof typeof PROMPT_GENERIC_INSTRUCTION];
